import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { API } from '../App';
import AppTopHeader from '../components/AppTopHeader';

const BuyQRCodePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [sessionChecking, setSessionChecking] = useState(true);
  const [pricing, setPricing] = useState({
    basePricePaise: 34900,
    offerPricePaise: null,
    offerEnabled: false,
  });

  const [form, setForm] = useState({
    quantity: 1,
    full_name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    locality: '',
    city: '',
    state: '',
    pincode: '',
  });

  const [pincodeLookupLoading, setPincodeLookupLoading] = useState(false);
  const [pincodeLookupMessage, setPincodeLookupMessage] = useState('');

  const unitPricePaise = useMemo(() => {
    if (pricing.offerEnabled && pricing.offerPricePaise) {
      return pricing.offerPricePaise;
    }
    return pricing.basePricePaise;
  }, [pricing]);

  const totalPricePaise = useMemo(() => unitPricePaise * Number(form.quantity || 1), [unitPricePaise, form.quantity]);

  const formatPrice = (paise) => Math.round((paise || 0) / 100);

  useEffect(() => {
    let isCancelled = false;

    const checkRecentSession = async () => {
      try {
        await axios.get(`${API}/auth/require-buy-reauth`);
        const profileResponse = await axios.get(`${API}/user/profile`);
        const userPhone = `${profileResponse.data?.user?.phone || ''}`.replace(/\D/g, '').slice(0, 10);

        setForm((previous) => ({
          ...previous,
          phone: userPhone,
        }));
      } catch (error) {
        if (isCancelled) return;

        localStorage.removeItem('token');
        toast.error('Please verify via OTP to continue with Buy QR.');
        navigate(`/login?next=${encodeURIComponent('/buy-qr?buy_reauth=1')}`, { replace: true });
        return;
      }

      if (!isCancelled) {
        setSessionChecking(false);
      }
    };

    checkRecentSession();

    return () => {
      isCancelled = true;
    };
  }, [location.search, navigate]);

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const response = await axios.get(`${API}/settings/public`);
        setPricing({
          basePricePaise: response.data.subscription_price_paise,
          offerPricePaise: response.data.offer_enabled ? response.data.offer_price_paise : null,
          offerEnabled: response.data.offer_enabled,
        });
      } catch (error) {
        // Keep defaults
      }
    };

    loadPricing();
  }, []);



  useEffect(() => {
    const pin = (form.pincode || '').trim();

    if (pin.length !== 6) {
      setPincodeLookupLoading(false);
      setPincodeLookupMessage('');
      return;
    }

    let isCancelled = false;
    const timer = setTimeout(async () => {
      try {
        setPincodeLookupLoading(true);
        setPincodeLookupMessage('');

        const response = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
        if (!response.ok) {
          throw new Error('Pincode lookup failed');
        }

        const data = await response.json();
        const result = Array.isArray(data) ? data[0] : null;

        if (!isCancelled) {
          if (result?.Status === 'Success' && Array.isArray(result.PostOffice) && result.PostOffice.length > 0) {
            const office = result.PostOffice[0];
            setForm((prev) => ({
              ...prev,
              city: office.District || prev.city,
              state: office.State || prev.state,
              locality: prev.locality || office.Name || prev.locality,
            }));
            setPincodeLookupMessage('City and state auto-filled from pincode.');
          } else {
            setPincodeLookupMessage('Could not find city/state for this pincode.');
          }
        }
      } catch (error) {
        if (!isCancelled) {
          setPincodeLookupMessage('Could not verify pincode right now.');
        }
      } finally {
        if (!isCancelled) {
          setPincodeLookupLoading(false);
        }
      }
    }, 300);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [form.pincode]);

  const updateForm = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };



  const ensureRazorpayLoaded = async () => {
    if (window.Razorpay) return;

    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const validateForm = () => {
    if (!form.full_name.trim()) return 'Please enter full name';
    if (!form.phone || form.phone.length !== 10) return 'Please enter a valid 10-digit phone number';
    if (!form.address_line1.trim()) return 'Please enter address line 1';
    if (!form.locality.trim()) return 'Please enter locality/area';
    if (!form.city.trim()) return 'Please enter city';
    if (!form.state.trim()) return 'Please enter state';
    if (!form.pincode || form.pincode.length !== 6) return 'Please enter a valid 6-digit pincode';
    if (!form.quantity || Number(form.quantity) < 1) return 'Please choose quantity';
    return null;
  };

  const handlePurchase = async (event) => {
    event.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setLoading(true);
    try {
      const orderResponse = await axios.post(`${API}/payment/create-order-for-physical-qr`, {
        quantity: Number(form.quantity),
        full_name: form.full_name.trim(),
        phone: form.phone,
        address_line1: form.address_line1.trim(),
        address_line2: form.address_line2.trim(),
        locality: form.locality.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        pincode: form.pincode,
      });

      const { order_id, amount, key_id, quantity } = orderResponse.data;
      await ensureRazorpayLoaded();

      const options = {
        key: key_id,
        amount,
        currency: 'INR',
        name: 'sideo',
        description: `QR Code Order (${quantity})`,
        order_id,
        prefill: {
          name: form.full_name,
          contact: `+91${form.phone}`,
        },
        theme: { color: '#1e3a8a' },
        handler: (paymentResponse) => {
          navigate('/buy-qr/processing', {
            replace: true,
            state: {
              paymentResponse,
              orderData: {
                display_order_id: orderResponse.data.display_order_id,
                quantity,
              },
            },
          });
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to initiate payment';

      if (
        error.response?.status === 401 ||
        detail.includes('Please login with OTP') ||
        detail.includes('does not match your logged-in account')
      ) {
        localStorage.removeItem('token');
        navigate(`/login?next=${encodeURIComponent('/buy-qr?buy_reauth=1')}`, { replace: true });
        return;
      }

      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  if (sessionChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white px-4 py-8 flex items-center justify-center">
        <div className="flex items-center gap-2 text-slate-700">
          <Loader2 className="w-5 h-5 animate-spin" />
          Verifying session...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <AppTopHeader backToHome backLabel="Back to Home" />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="mb-4">
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow' }}>
                Buy QR Code
              </h1>
              <p className="text-slate-600">
                Place your order and we will deliver QR Code stickers to your address.
              </p>
            </div>

            <form className="space-y-4" onSubmit={handlePurchase}>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mobile Number</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-slate-200 bg-slate-50 text-slate-600">
                    +91
                  </span>
                  <input
                    type="tel"
                    value={form.phone}
                    readOnly
                    className="w-full h-11 rounded-r-lg border border-slate-200 px-3 bg-slate-50 text-slate-600"
                    required
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500">Verified number from your OTP login.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Quantity</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={form.quantity}
                  onChange={(event) => updateForm('quantity', event.target.value.replace(/\D/g, ''))}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={(event) => updateForm('full_name', event.target.value)}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Pincode</label>
                <input
                  type="text"
                  value={form.pincode}
                  onChange={(event) => updateForm('pincode', event.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3"
                  required
                />
                {pincodeLookupLoading && (
                  <p className="mt-1 text-xs text-slate-500">Fetching city/state...</p>
                )}
                {!pincodeLookupLoading && pincodeLookupMessage && (
                  <p className="mt-1 text-xs text-slate-500">{pincodeLookupMessage}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 1</label>
                <input
                  type="text"
                  value={form.address_line1}
                  onChange={(event) => updateForm('address_line1', event.target.value)}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3"
                  placeholder="Enter your address"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 2 (Optional)</label>
                <input
                  type="text"
                  value={form.address_line2}
                  onChange={(event) => updateForm('address_line2', event.target.value)}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Locality / Area</label>
                <input
                  type="text"
                  value={form.locality}
                  onChange={(event) => updateForm('locality', event.target.value)}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3"
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={(event) => updateForm('city', event.target.value)}
                    className="w-full h-11 rounded-lg border border-slate-200 px-3"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
                  <input
                    type="text"
                    value={form.state}
                    onChange={(event) => updateForm('state', event.target.value)}
                    className="w-full h-11 rounded-lg border border-slate-200 px-3"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
                <input
                  type="text"
                  value="India"
                  readOnly
                  className="w-full h-11 rounded-lg border border-slate-200 px-3 bg-slate-50 text-slate-600"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary h-12 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Pay ₹${formatPrice(totalPricePaise)} and Place Order`
                )}
              </button>
            </form>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Barlow' }}>
                Order Summary
              </h2>
              <div className="space-y-2 text-sm text-slate-700">
                <div className="flex justify-between">
                  <span>Price per QR Code</span>
                  <span>₹{formatPrice(unitPricePaise)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Quantity</span>
                  <span>{Number(form.quantity) || 1}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-semibold text-slate-900">
                  <span>Total</span>
                  <span>₹{formatPrice(totalPricePaise)}</span>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Secure payment via Razorpay.
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyQRCodePage;
